import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useApp } from '../context/AppContext';
import { Mail, Trash2, Send, CheckCheck, Clock, Reply, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminMessagesPage = () => {
  const { getAuthHeaders, language } = useApp();
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [apartments, setApartments] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    fetchMessages();
    fetchApartments();
  }, []);

  const fetchMessages = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/contact/messages`, {
        headers: getAuthHeaders(),
      });
      setMessages(data);
    } catch (error) {
      toast.error('Failed to fetch messages');
    }
  };

  const fetchApartments = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/apartments`);
      setApartments(data);
    } catch (error) {
      console.error('Failed to fetch apartments');
    }
  };

  const getApartmentTitle = (apartmentId) => {
    if (!apartmentId) return null;
    const apartment = apartments.find(apt => apt.id === apartmentId);
    if (!apartment) return apartmentId;
    return apartment.title?.[language] || apartment.title?.en || apartmentId;
  };

  const getApartmentDetails = (apartmentId) => {
    if (!apartmentId) return null;
    const apartment = apartments.find(apt => apt.id === apartmentId);
    return apartment;
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ștergeți acest mesaj? / Delete this message?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/contact/messages/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success('Message deleted!');
      fetchMessages();
      if (selectedMessage?.id === id) {
        setSelectedMessage(null);
      }
      setSelectedIds(selectedIds.filter(sid => sid !== id));
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      toast.error('No messages selected');
      return;
    }

    if (!window.confirm(`Ștergeți ${selectedIds.length} mesaje? / Delete ${selectedIds.length} messages?`)) return;
    
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/contact/messages/delete-multiple`,
        { ids: selectedIds },
        { headers: getAuthHeaders() }
      );
      toast.success(`${data.deleted_count} messages deleted!`);
      fetchMessages();
      setSelectedIds([]);
      if (selectedMessage && selectedIds.includes(selectedMessage.id)) {
        setSelectedMessage(null);
      }
    } catch (error) {
      toast.error('Failed to delete messages');
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === messages.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(messages.map(msg => msg.id));
    }
  };

  const toggleSelectMessage = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(sid => sid !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleSelectMessage = async (msg) => {
    setSelectedMessage(msg);
    setShowReplyForm(false);
    
    // Mark as read if not already read
    if (!msg.read) {
      try {
        await axios.put(`${BACKEND_URL}/api/contact/messages/${msg.id}/read`, {}, {
          headers: getAuthHeaders(),
        });
        // Update local state
        setMessages(messages.map(m => m.id === msg.id ? { ...m, read: true } : m));
      } catch (error) {
        console.error('Failed to mark message as read');
      }
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/contact/messages/mark-all-read`, {}, {
        headers: getAuthHeaders(),
      });
      toast.success(`${data.count} mesaje marcate ca citite`);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to mark messages as read');
    }
  };

  const openReplyForm = () => {
    if (!selectedMessage) return;
    setReplySubject(`Re: ${selectedMessage.subject}`);
    setReplyBody('');
    setShowReplyForm(true);
  };

  const handleSendReply = async () => {
    if (!selectedMessage || !replyBody.trim()) {
      toast.error('Completați mesajul de răspuns');
      return;
    }

    setSendingReply(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact/messages/reply`, {
        message_id: selectedMessage.id,
        reply_subject: replySubject,
        reply_body: replyBody
      }, {
        headers: getAuthHeaders(),
      });
      
      toast.success(`Răspuns trimis către ${selectedMessage.email}`);
      setShowReplyForm(false);
      setReplyBody('');
      
      // Update local state to show replied status
      setMessages(messages.map(m => m.id === selectedMessage.id ? { ...m, replied: true, read: true } : m));
      setSelectedMessage({ ...selectedMessage, replied: true, read: true });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send reply';
      toast.error(errorMsg);
    } finally {
      setSendingReply(false);
    }
  };

  const unreadCount = messages.filter(m => !m.read).length;

  return (
    <AdminLayout>
      <div data-testid="admin-messages-page">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <h1 className="text-4xl font-serif">Contact Messages</h1>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full animate-pulse">
                {unreadCount} noi
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <Button onClick={handleMarkAllRead} variant="outline" size="sm">
              <CheckCheck size={16} className="mr-2" />
              Marchează toate ca citite
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Messages List */}
          <div className="lg:col-span-1 glass p-4 space-y-2 max-h-[600px] overflow-y-auto">
            {/* Select All and Delete Selected */}
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/10">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedIds.length === messages.length && messages.length > 0}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-white/20"
                />
                <span>Select All ({messages.length})</span>
              </label>
              {selectedIds.length > 0 && (
                <Button
                  onClick={handleDeleteSelected}
                  variant="destructive"
                  size="sm"
                  data-testid="delete-selected-button"
                >
                  <Trash2 size={14} className="mr-2" />
                  Delete ({selectedIds.length})
                </Button>
              )}
            </div>

            {messages.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-12">No messages</p>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`relative transition-colors ${
                    selectedMessage?.id === msg.id 
                      ? 'bg-primary/10 border-l-2 border-primary' 
                      : !msg.read 
                        ? 'bg-red-500/5 border-l-2 border-red-500 hover:bg-red-500/10' 
                        : 'bg-slate-900/50 hover:bg-slate-900'
                  }`}
                >
                  <div className="flex items-start gap-3 p-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(msg.id)}
                      onChange={() => toggleSelectMessage(msg.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 mt-1 rounded border-white/20"
                    />
                    <button
                      data-testid={`message-item-${msg.id}`}
                      onClick={() => handleSelectMessage(msg)}
                      className="flex-1 text-left"
                    >
                      <div className="flex items-start gap-3">
                        <div className="relative">
                          <Mail size={16} className={!msg.read ? 'text-red-400' : msg.replied ? 'text-green-400' : 'text-primary'} />
                          {!msg.read && (
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm truncate ${!msg.read ? 'font-bold' : 'font-semibold'}`}>{msg.name}</p>
                            {msg.replied && (
                              <span className="text-xs text-green-400 flex items-center gap-1">
                                <CheckCheck size={12} /> răspuns
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">{msg.subject}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(msg.created_at).toLocaleDateString()}
                            {!msg.read && <span className="ml-2 text-red-400 font-medium">• NOU</span>}
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Message Detail */}
          <div className="lg:col-span-2 glass p-8">
            {!selectedMessage ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                Select a message to view details
              </div>
            ) : (
              <div data-testid="message-detail">
                <div className="flex items-start justify-between mb-6 pb-6 border-b border-white/10">
                  <div className="flex-1">
                    <h2 className="text-2xl font-serif mb-4">{selectedMessage.subject}</h2>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-xs uppercase tracking-wider text-primary">From</span>
                        <p className="mt-1">{selectedMessage.name}</p>
                        <p className="text-muted-foreground">{selectedMessage.email}</p>
                      </div>
                      {selectedMessage.phone && (
                        <div>
                          <span className="text-xs uppercase tracking-wider text-primary">Phone</span>
                          <p className="mt-1">
                            <a href={`tel:${selectedMessage.phone}`} className="text-foreground hover:text-primary transition-colors">
                              {selectedMessage.phone}
                            </a>
                          </p>
                        </div>
                      )}
                      <div>
                        <span className="text-xs uppercase tracking-wider text-primary">Date</span>
                        <p className="mt-1">{new Date(selectedMessage.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                    {selectedMessage.apartment_id && (
                      <div className="mt-4 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                        <span className="text-xs uppercase tracking-wider text-primary block mb-3">Regarding Apartment</span>
                        {getApartmentDetails(selectedMessage.apartment_id) ? (
                          <>
                            <div className="flex items-start gap-4">
                              {getApartmentDetails(selectedMessage.apartment_id).images?.[0] && (
                                <img 
                                  src={getApartmentDetails(selectedMessage.apartment_id).images[0]} 
                                  alt="Apartment"
                                  className="w-20 h-20 object-cover rounded"
                                />
                              )}
                              <div className="flex-1">
                                <p className="text-base font-semibold mb-1">
                                  {getApartmentTitle(selectedMessage.apartment_id)}
                                </p>
                                <p className="text-sm text-primary font-semibold">
                                  €{getApartmentDetails(selectedMessage.apartment_id).price_eur}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Status: {getApartmentDetails(selectedMessage.apartment_id).status}
                                </p>
                              </div>
                            </div>
                            <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-white/10">
                              ID: {selectedMessage.apartment_id}
                            </p>
                            <a 
                              href={`/apartments/${selectedMessage.apartment_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block mt-3 text-xs text-primary hover:underline"
                            >
                              View Apartment →
                            </a>
                          </>
                        ) : (
                          <p className="text-sm">ID: {selectedMessage.apartment_id}</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={openReplyForm}
                      variant="default"
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Reply size={16} className="mr-2" />
                      Răspunde
                    </Button>
                    <Button
                      onClick={() => handleDelete(selectedMessage.id)}
                      data-testid="delete-message-button"
                      variant="destructive"
                      size="sm"
                    >
                      <Trash2 size={16} className="mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>

                {/* Status badges */}
                {(selectedMessage.replied || selectedMessage.read) && (
                  <div className="flex gap-2 mb-4">
                    {selectedMessage.read && (
                      <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded flex items-center gap-1">
                        <CheckCheck size={12} /> Citit
                      </span>
                    )}
                    {selectedMessage.replied && (
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded flex items-center gap-1">
                        <Send size={12} /> Răspuns trimis
                        {selectedMessage.replied_at && (
                          <span className="ml-1 text-green-400/70">
                            ({new Date(selectedMessage.replied_at).toLocaleDateString()})
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                )}

                <div>
                  <span className="text-xs uppercase tracking-wider text-primary">Message</span>
                  <p className="text-sm text-muted-foreground mt-3 whitespace-pre-line leading-relaxed">
                    {selectedMessage.message}
                  </p>
                </div>

                {/* Reply Form */}
                {showReplyForm && (
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <Reply size={18} /> Răspunde la {selectedMessage.name}
                      </h3>
                      <Button
                        onClick={() => setShowReplyForm(false)}
                        variant="ghost"
                        size="sm"
                      >
                        <X size={16} />
                      </Button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="text-xs uppercase tracking-wider text-primary block mb-2">
                          Către
                        </label>
                        <input
                          type="text"
                          value={selectedMessage.email}
                          disabled
                          className="w-full px-4 py-2 bg-slate-800 border border-white/10 rounded text-muted-foreground"
                        />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-primary block mb-2">
                          Subiect
                        </label>
                        <input
                          type="text"
                          value={replySubject}
                          onChange={(e) => setReplySubject(e.target.value)}
                          className="w-full px-4 py-2 bg-slate-900 border border-white/10 rounded focus:border-primary focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-primary block mb-2">
                          Mesaj
                        </label>
                        <textarea
                          value={replyBody}
                          onChange={(e) => setReplyBody(e.target.value)}
                          rows={6}
                          placeholder="Scrieți răspunsul aici..."
                          className="w-full px-4 py-2 bg-slate-900 border border-white/10 rounded focus:border-primary focus:outline-none resize-none"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button
                          onClick={() => setShowReplyForm(false)}
                          variant="outline"
                        >
                          Anulează
                        </Button>
                        <Button
                          onClick={handleSendReply}
                          disabled={sendingReply || !replyBody.trim()}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {sendingReply ? (
                            <>
                              <Clock size={16} className="mr-2 animate-spin" />
                              Se trimite...
                            </>
                          ) : (
                            <>
                              <Send size={16} className="mr-2" />
                              Trimite răspuns
                            </>
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        ⚠️ Asigurați-vă că ați configurat setările SMTP în Admin → Email Settings pentru a putea trimite email-uri.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminMessagesPage;